import { useState, useCallback } from "react";
import { estimatesApi } from "../services/api";

/**
 * Custom hook for uploading estimates
 * Handles uploading both images and saving the AI dataset in one operation
 * 
 * @returns {Object} - { mutateAsync, isPending, error, data }
 * 
 * @example
 * const uploadMutation = useUploadEstimate();
 * 
 * const handleSave = async () => {
 *   const result = await uploadMutation.mutateAsync({
 *     image: canvasImage,
 *     original: originalImage,
 *     metadata: { mapBounds, mapCenter, address, mode, estimate }
 *   });
 * };
 */
export const useUploadEstimate = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const mutateAsync = useCallback(async ({ image, original, metadata }) => {
    setIsPending(true);
    setError(null);

    try {
      // Upload both images in parallel for better performance
      const [imageMeta, originalMeta] = await Promise.all([
        estimatesApi.uploadImage(image),
        estimatesApi.uploadImage(original),
      ]);

      // Build AI dataset payload
      const aiPayload = {
        dataset: {
          originalUrl: originalMeta.Location,
          measuredUrl: imageMeta.Location,
          ...metadata,
        },
      };

      // Save the dataset
      await estimatesApi.uploadDataset(aiPayload);

      const result = { imageMeta, originalMeta };
      setData(result);
      return result;
    } catch (err) {
      setError(err);
      console.error("Failed to upload estimate:", err);
      throw err;
    } finally {
      setIsPending(false);
    }
  }, []);

  return { mutateAsync, isPending, error, data };
};

export default useUploadEstimate;
